/**
 * PriceHistorySettings — Settings › Price history (Stock-In Safety Engine, design §8).
 *
 * For every ingredient: its status, its usual price range per kg / L / piece, and its last
 * five purchases — with anything the service team still has to look at shown in red. Each
 * price is the purchase line's own (what the bill said), as the entry guard judged it.
 *
 * READ ONLY. Nothing on this screen changes anything: corrections, reverts, approvals and
 * resets happen on the service team's own console (cx-admin, localhost), never here.
 * Shown only when cx_settings.history_enabled is on for the business.
 *
 * Colours (§8.2): grey "Learning n/5" · red row = a red entry waiting for review · amber cell
 * = a small doubt · green "Protected" / "Provisional" · red cell + badge = force-saved or
 * wrong · grey struck through = reverted or excluded · "estimated" = a correction that used
 * rebuilt history touched it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { cxPriceHistory, cxPriceHistoryItem } from '../services/cxGuardClient';
import { cxDay, cxMoney } from '../utils/cxGuardForm';

const PAGE = 60;
const STATUS_LABELS = { learning: 'Learning', needs_review: 'Waiting for review', protected: 'Protected', provisional: 'Provisional' };

const qtyText = (q, unit) => {
  if (q == null) return null;
  const n = Number(q);
  return `${Number.isInteger(n) ? n : +n.toFixed(3)} ${unit || ''}`.trim();
};

function statusStyle(row) {
  if (row.status === 'protected' || row.status === 'provisional') return { ...pill, background: '#dcfce7', color: '#166534' };
  if (row.status === 'deleted') return { ...pill, background: '#f3f4f6', color: '#6b7280' };
  return { ...pill, background: '#f3f4f6', color: '#374151' };
}

const LOOK = {
  ok: { background: '#fff', borderColor: '#e5e7eb', color: '#111827' },
  amber: { background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' },
  red: { background: '#fef2f2', borderColor: '#fca5a5', color: '#b91c1c' },
  pending: { background: '#f9fafb', borderColor: '#e5e7eb', color: '#6b7280' },
};

export function EntryCell({ cell, unit }) {
  const look = LOOK[cell.look] || LOOK.ok;
  return (
    <div data-testid={`ph-cell-${cell.check_id}`} data-look={cell.look}
      style={{ ...cellBox, background: look.background, borderColor: look.borderColor, color: look.color }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, fontSize: 11.5, color: '#6b7280' }}>
        <span>{cxDay(cell.day) || '—'}</span>
        {cell.estimated && <span style={estimatedTag}>estimated</span>}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>
        {cell.per_display != null ? `${cxMoney(cell.per_display)}/${unit}` : '—'}
      </div>
      <div style={{ fontSize: 12, color: '#4b5563' }}>{qtyText(cell.quantity_display, unit) || (cell.look === 'pending' ? 'Not checked yet' : '')}</div>
      {cell.vendor && <div style={{ fontSize: 11.5, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cell.vendor}>{cell.vendor}</div>}
      {cell.badges && cell.badges.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
          {cell.badges.map((b) => (
            <span key={b} style={{ ...badge, ...(b === 'Verified' ? { background: '#dcfce7', color: '#166534' } : {}) }}>{b}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function Flagged({ row }) {
  const [open, setOpen] = useState(false);
  if (!row.flagged.length) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <button type="button" style={linkBtn} aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? '▾' : '▸'} Flagged entries ({row.flagged.length})
      </button>
      {open && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13, color: '#b91c1c' }}>
          {row.flagged.map((f) => (
            <li key={f.check_id} style={{ marginBottom: 4 }}>
              {cxDay(f.day)} · {f.per_display != null ? `${cxMoney(f.per_display)}/${row.unit}` : '—'}
              {f.quantity_display != null ? ` · ${qtyText(f.quantity_display, row.unit)}` : ''}
              {f.vendor ? ` · ${f.vendor}` : ''} · {f.badges.join(', ') || 'Needs review'}
              {f.force_reason ? ` · confirmed by typing "${f.force_reason}"` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const STATE_LABEL = { reverted: 'Reverted', partly_reverted: 'Partly reverted', excluded: 'Excluded' };

function FullHistory({ itemId, unit }) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let alive = true;
    cxPriceHistoryItem(itemId)
      .then((d) => { if (alive) setState({ data: d }); })
      .catch(() => { if (alive) setState({ error: 'Could not load the full history. Try again in a moment.' }); });
    return () => { alive = false; };
  }, [itemId]);
  if (state.loading) return <div style={{ ...muted, marginTop: 8 }}>Loading full history…</div>;
  if (state.error || !state.data || state.data.enabled === false) return <div style={{ ...muted, marginTop: 8, color: '#b91c1c' }}>{state.error || 'Not available.'}</div>;
  const d = state.data;
  return (
    <div data-testid={`ph-history-${itemId}`} style={{ marginTop: 10, borderTop: '1px dashed #e5e7eb', paddingTop: 10 }}>
      {d.window && d.window.reasons.length > 0 && (
        <div style={{ ...muted, marginBottom: 8 }}>Why: {d.window.reasons.join('; ')}</div>
      )}
      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Every purchase ({d.history.length})</div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 13 }}>
        {d.history.map((h) => {
          const gone = h.state !== 'active';
          const color = gone ? '#9ca3af' : h.look === 'red' ? '#b91c1c' : h.look === 'amber' ? '#92400e' : '#111827';
          return (
            <li key={h.check_id} data-state={h.state} style={{ padding: '4px 0', color, borderBottom: '1px solid #f3f4f6' }}>
              <span style={gone ? { textDecoration: 'line-through' } : undefined}>
                {cxDay(h.day) || '—'} · {h.per_display != null ? `${cxMoney(h.per_display)}/${unit}` : '—'}
                {h.quantity_display != null ? ` · ${qtyText(h.quantity_display, unit)}` : ''}{h.vendor ? ` · ${h.vendor}` : ''}
              </span>
              {gone && <span style={{ ...badge, background: '#f3f4f6', color: '#6b7280', marginLeft: 6 }}>{STATE_LABEL[h.state]}</span>}
              {h.badges.map((b) => <span key={b} style={{ ...badge, marginLeft: 6 }}>{b}</span>)}
              {h.in_usual && !gone && <span style={{ ...badge, background: '#dcfce7', color: '#166534', marginLeft: 6 }}>counts toward usual</span>}
              {h.estimated && <span style={{ ...estimatedTag, marginLeft: 6 }}>estimated</span>}
            </li>
          );
        })}
      </ul>
      {d.corrections.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: '10px 0 4px' }}>Corrections by the service team</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#374151' }}>
            {d.corrections.map((c) => (
              <li key={c.correction_id} style={c.undone_at ? { color: '#9ca3af', textDecoration: 'line-through' } : undefined}>
                {cxDay(String(c.when || '').slice(0, 10))} · {c.action === 'correct' ? 'Corrected' : c.action === 'revert' ? 'Unused stock removed' : c.action}
                {c.price_per_display != null ? ` to ${cxMoney(c.price_per_display)}/${unit}` : ''}
                {c.quantity_display != null ? `, quantity ${qtyText(c.quantity_display, unit)}` : ''} — {c.reason}
                {c.estimated ? ' (estimated history included)' : ''}{c.undone_at ? ' · undone' : ''}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export function PriceRow({ row }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`ph-row-${row.item_id}`} data-needs-review={row.needs_review ? 'yes' : 'no'}
      style={{ ...card, borderLeft: `4px solid ${row.needs_review ? '#dc2626' : row.status === 'protected' || row.status === 'provisional' ? '#16a34a' : '#d1d5db'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
            {row.name} <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 13 }}>· per {row.unit || '—'}</span>
          </div>
          <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>
            {row.usual
              ? <>Usual: {cxMoney(row.usual.low)} – <b>{cxMoney(row.usual.median)}</b> – {cxMoney(row.usual.high)} per {row.unit}</>
              : <span style={muted}>{row.status === 'learning' ? 'Still learning what is usual' : row.status === 'needs_review' ? 'Usual price waiting for a check by the service team' : '—'}</span>}
            {row.category ? <span style={muted}> · {row.category}</span> : null}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {row.needs_review && <span style={{ ...pill, background: '#fee2e2', color: '#b91c1c' }}>Needs review</span>}
          {row.inactive && <span style={{ ...pill, background: '#f3f4f6', color: '#6b7280' }}>{row.inactive === 'merged' ? `Merged into ${row.merged_into || 'another item'}` : row.inactive === 'deleted' ? 'Deleted' : 'Inactive'}</span>}
          <span style={statusStyle(row)}>{row.label}</span>
        </div>
      </div>
      {row.cells.length > 0 ? (
        <div style={cellGrid}>
          {row.cells.map((c) => <EntryCell key={c.check_id} cell={c} unit={row.unit} />)}
        </div>
      ) : (
        <div style={{ ...muted, marginTop: 8 }}>No purchases recorded yet.</div>
      )}
      {row.inactive === 'deleted' && row.correctable === false && (
        <div style={{ ...muted, marginTop: 6 }}>This item was deleted with its stock, so these entries can no longer be corrected.</div>
      )}
      <Flagged row={row} />
      {row.entry_count > 0 && (
        <button type="button" style={{ ...linkBtn, marginTop: 6 }} aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? '▾ Hide full history' : '▸ Full history and corrections'}
        </button>
      )}
      {open && <FullHistory itemId={row.item_id} unit={row.unit} />}
    </div>
  );
}

/** PURE — the filters of the top bar (exported for the tests). */
export function filterRows(rows, { search = '', category = '', status = '', redOnly = false }) {
  const q = search.trim().toLowerCase();
  return rows.filter((r) => (!q || r.name.toLowerCase().includes(q))
    && (!category || r.category === category)
    && (!status || r.status === status)
    && (!redOnly || r.needs_review));
}

const PriceHistorySettings = ({ onClose }) => {
  const [state, setState] = useState({ loading: true });
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [redOnly, setRedOnly] = useState(false);
  const [shown, setShown] = useState(PAGE);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    let alive = true;
    cxPriceHistory()
      .then((d) => { if (alive) setState({ data: d }); })
      .catch(() => { if (alive) setState({ error: 'Could not load the price history. Check your connection and open it again.' }); });
    return () => { alive = false; };
  }, []);

  const data = state.data;
  const rows = useMemo(() => (data && data.items ? filterRows(data.items, { search, category, status, redOnly }) : []), [data, search, category, status, redOnly]);
  useEffect(() => { setShown(PAGE); }, [search, category, status, redOnly]);

  return (
    <div style={overlay} role="dialog" aria-label="Price history">
      <div style={sheet}>
        <div style={sheetHeader}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>Price history</h2>
            <div style={{ ...muted, marginTop: 2 }}>What each ingredient usually costs, its last five purchases, and anything that needs a look</div>
          </div>
          <button type="button" aria-label="Close" style={closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={sheetBody}>
          {state.loading && <div style={muted}>Loading…</div>}
          {state.error && <div role="alert" style={{ ...infoBox, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>{state.error}</div>}
          {data && data.enabled === false && (
            <div style={infoBox}>Price history is not switched on for this business yet.</div>
          )}
          {data && data.enabled && (
            <>
              <div style={{ ...infoBox, marginBottom: 12 }}>
                Prices are what your purchase bills said, per kg, L or piece. Red marks an entry that was
                force-saved or looks wrong — your support team reviews these. Nothing here can be changed from this screen.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <input type="search" aria-label="Search ingredient" placeholder="Search ingredient" value={search}
                  onChange={(e) => setSearch(e.target.value)} style={{ ...input, flex: '1 1 180px' }} />
                <select aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)} style={input}>
                  <option value="">All categories</option>
                  {data.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)} style={input}>
                  <option value="">All statuses</option>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: '#374151', cursor: 'pointer' }}>
                  <input type="checkbox" checked={redOnly} onChange={(e) => setRedOnly(e.target.checked)} /> Red only
                </label>
                <span data-testid="ph-counter" style={{ ...pill, background: data.counters.need_review ? '#fee2e2' : '#dcfce7', color: data.counters.need_review ? '#b91c1c' : '#166534' }}>
                  {data.counters.need_review} ingredient{data.counters.need_review === 1 ? '' : 's'} need{data.counters.need_review === 1 ? 's' : ''} review
                </span>
              </div>

              {rows.length === 0 && <div style={muted}>{data.items.length ? 'No ingredient matches these filters.' : 'No ingredients yet.'}</div>}
              {rows.slice(0, shown).map((r) => <PriceRow key={r.item_id} row={r} />)}
              {rows.length > shown && (
                <button type="button" style={{ ...ghostBtn, width: '100%' }} onClick={() => setShown(shown + PAGE)}>
                  Show more ({rows.length - shown} left)
                </button>
              )}

              {data.inactive.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <button type="button" style={linkBtn} aria-expanded={showInactive} onClick={() => setShowInactive(!showInactive)}>
                    {showInactive ? '▾' : '▸'} Inactive items ({data.inactive.length}) — deleted, merged or switched off
                  </button>
                  {showInactive && <div style={{ marginTop: 8 }}>{data.inactive.map((r) => <PriceRow key={`in-${r.item_id}`} row={r} />)}</div>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── styles: inline, as Settings › Piece Guard ───────────────────────────────────────────
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 10001,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
};
const sheet = {
  background: '#fff', borderRadius: 14, width: '100%', maxWidth: 1000, maxHeight: '94vh',
  display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', fontFamily: 'Roboto, sans-serif',
};
const sheetHeader = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
  padding: '16px 18px 12px', borderBottom: '1px solid #e5e7eb',
};
const sheetBody = { padding: '14px 18px 18px', overflowY: 'auto' };
const muted = { fontSize: 12.5, color: '#6b7280', lineHeight: 1.45 };
const input = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#fff' };
const infoBox = {
  padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a',
  borderRadius: 10, fontSize: 13, lineHeight: 1.5,
};
const card = { border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', background: '#fff', marginBottom: 10 };
const cellGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginTop: 10 };
const cellBox = { borderWidth: 1, borderStyle: 'solid', borderRadius: 8, padding: '7px 9px', minWidth: 0 };
const pill = { padding: '3px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' };
const badge = { padding: '1px 7px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: '#fee2e2', color: '#b91c1c', whiteSpace: 'nowrap' };
const estimatedTag = { padding: '0 6px', borderRadius: 999, fontSize: 10.5, background: '#ede9fe', color: '#5b21b6' };
const linkBtn = { background: 'none', border: 'none', color: '#2563eb', fontSize: 13, cursor: 'pointer', padding: 0 };
const ghostBtn = {
  padding: '8px 13px', background: '#fff', color: '#374151', border: '1px solid #d1d5db',
  borderRadius: 8, fontSize: 13.5, cursor: 'pointer',
};
const closeBtn = { border: 'none', background: 'transparent', fontSize: 26, lineHeight: 1, cursor: 'pointer', color: '#6b7280' };

export default PriceHistorySettings;
